
import React, { useState, useEffect } from 'react';
import { X, MessageSquare, ArrowRightCircle, Star, Heart, User, ChevronRight, Award } from 'lucide-react';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { COLORS, USERS_COLLECTION } from '../../config/constants';
import { useAppContext } from '../../App.jsx';
import { collection, query, orderBy, getDocs, limit } from 'firebase/firestore';
import AllReviewsModal from './AllReviewsModal';

// Função utilitária para obter dados do Rank
export const getRankData = (completedServices = 0) => {
    if (completedServices >= 1000) return { label: 'Elite T-SERVI', color: '#7C3AED', bg: 'bg-purple-100', text: 'text-purple-700' };
    if (completedServices >= 500) return { label: 'Mestre', color: '#2563EB', bg: 'bg-blue-100', text: 'text-blue-700' };
    if (completedServices >= 250) return { label: 'Especialista', color: '#059669', bg: 'bg-emerald-100', text: 'text-emerald-700' };
    if (completedServices >= 100) return { label: 'Experiente', color: '#D97706', bg: 'bg-amber-100', text: 'text-amber-700' };
    return { label: 'Novato', color: '#6B7280', bg: 'bg-gray-100', text: 'text-gray-600' };
};

const ReviewItem = ({ review }) => (
    <div className="border-b last:border-0 py-4">
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
                <div className="bg-gray-100 p-1.5 rounded-full mr-2">
                    <User size={14} className="text-gray-500" />
                </div>
                <span className="text-sm font-bold text-gray-800">{review.raterName || 'Usuário'}</span>
            </div>
            <span className="text-xs text-gray-400">
                {review.createdAt?.toDate ? review.createdAt.toDate().toLocaleDateString('pt-BR') : ''}
            </span>
        </div>
        <div className="flex items-center mb-2">
            {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} className={i < review.rating ? "text-yellow-400 fill-current" : "text-gray-300"} />
            ))}
        </div>
        <p className="text-sm text-gray-700 italic line-clamp-2">"{review.comment || 'Sem comentário.'}"</p>
    </div>
);

const ServiDetailModal = ({ servi, existingChatId, onClose, onStartChat, onOpenChat, fullUserData }) => {
    const { user, db, updateUserProfile, showMessage } = useAppContext();
    const [serviceTitle, setServiceTitle] = useState('');
    const [activeTab, setActiveTab] = useState('about');
    const [openLightbox, setOpenLightbox] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [isFavoriting, setIsFavoriting] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [isLoadingReviews, setIsLoadingReviews] = useState(false);
    const [isAllReviewsModalOpen, setIsAllReviewsModalOpen] = useState(false);

    const profile = servi?.profile_servi || {};
    const serviId = servi?.id || null;
    const completedServices = profile.completedServices || 0;
    const rank = getRankData(completedServices);

    useEffect(() => {
        if (activeTab === 'reviews' && serviId && db) {
            const fetchReviews = async () => {
                setIsLoadingReviews(true);
                try {
                    const ratingsRef = collection(db, USERS_COLLECTION, serviId, 'ratings');
                    const q = query(ratingsRef, orderBy('createdAt', 'desc'), limit(5));
                    const querySnapshot = await getDocs(q);
                    const reviewsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setReviews(reviewsData);
                } catch (error) {
                    console.error("Erro ao buscar avaliações:", error);
                } finally {
                    setIsLoadingReviews(false);
                }
            };
            fetchReviews();
        }
    }, [activeTab, serviId, db]);

    const handleStartChat = () => {
        if (!existingChatId && serviceTitle.trim() === '') {
            showMessage('Por favor, descreva o serviço que você precisa.', 'warning');
            return;
        }
        onStartChat(serviceTitle);
    };

    const isFavorited = fullUserData?.profile_client?.favoriteServis?.includes(serviId);

    const handleFavorite = async () => {
        if (!user || !fullUserData?.profile_client || !serviId || isFavoriting) return;
        setIsFavoriting(true);
        const currentFavorites = fullUserData.profile_client.favoriteServis || [];
        const updatedFavorites = isFavorited
            ? currentFavorites.filter(id => id !== serviId)
            : [...currentFavorites, serviId];
        const updatedProfile = { ...fullUserData.profile_client, favoriteServis: updatedFavorites };
        await updateUserProfile(user.uid, 'client', updatedProfile);
        setIsFavoriting(false);
    };

    const displayName = profile.companyName || profile.name || 'Profissional';
    const slides = (profile.gallery || []).map(item => ({ src: item.url }));

    const statusConfig = {
        'Disponível': { text: 'Disponível', color: COLORS.SUCCESS_GREEN, textColor: 'white' },
        'Agendamento': { text: 'Agendamento', color: COLORS.PRIMARY_BLUE, textColor: 'white' },
        'Indisponível': { text: 'Indisponível', color: '#6B7280', textColor: 'white' },
    };
    const currentStatus = statusConfig[profile.status] || statusConfig['Indisponível'];

    const isUnavailable = profile.status === 'Indisponível';

    const renderTabContent = () => {
        switch (activeTab) {
            case 'portfolio':
                return (
                    <div className="grid grid-cols-3 gap-2">
                        {(profile.gallery && profile.gallery.length > 0) ? (
                            profile.gallery.map((item, index) => (
                                <div key={index} className="aspect-square bg-gray-200 rounded-lg overflow-hidden cursor-pointer" onClick={() => { setLightboxIndex(index); setOpenLightbox(true); }}>
                                    <img src={item.url} alt={`Portfolio item ${index + 1}`} className="w-full h-full object-cover" />
                                </div>
                            ))
                        ) : (
                            <p className="col-span-3 text-center text-gray-500 py-4">Nenhum item no portfólio.</p>
                        )}
                    </div>
                );
            case 'reviews':
                return (
                    <div className="space-y-2">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Últimos Depoimentos</h3>
                            {reviews.length > 0 && (
                                <button
                                    onClick={() => setIsAllReviewsModalOpen(true)}
                                    className="text-[10px] font-black text-blue-600 uppercase tracking-tighter flex items-center hover:bg-blue-50 px-2 py-1 rounded-lg transition-all"
                                >
                                    Ver Todas <ChevronRight size={10} className="ml-1" />
                                </button>
                            )}
                        </div>

                        {isLoadingReviews ? (
                            <p className="text-center text-gray-500 py-4">Carregando avaliações...</p>
                        ) : reviews.length > 0 ? (
                            <>
                                {reviews.map(review => <ReviewItem key={review.id} review={review} />)}
                                {profile.ratingCount > 5 && (
                                    <button
                                        onClick={() => setIsAllReviewsModalOpen(true)}
                                        className="w-full mt-4 py-3 border-2 border-blue-50 text-blue-600 text-xs font-bold rounded-xl flex items-center justify-center hover:bg-blue-50 transition-all"
                                    >
                                        Ver todas as {profile.ratingCount} avaliações
                                    </button>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-8">
                                <Star className="w-12 h-12 text-gray-200 mx-auto mb-2" />
                                <p className="text-gray-500">Nenhuma avaliação ainda.</p>
                            </div>
                        )}
                    </div>
                );
            case 'about':
            default:
                return (
                    <div className="prose prose-sm max-w-none">
                        <p className="whitespace-pre-line">{profile.description || 'Sem descrição.'}</p>
                    </div>
                );
        }
    };

    if (!servi) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={onClose}>
                <div className="bg-gray-50 rounded-2xl w-full max-w-lg relative animate-slide-up flex flex-col" style={{ maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>

                    {/* Elementos de Status e Botões no Topo */}
                    <div className="absolute top-4 left-4 z-10 flex flex-col space-y-2">
                        <span className="px-3 py-1 text-xs font-bold rounded-full shadow-sm w-fit" style={{ backgroundColor: currentStatus.color, color: currentStatus.textColor }}>
                            {currentStatus.text}
                        </span>
                        {/* SELO DE RANKING */}
                        <div className={`flex items-center px-2 py-0.5 rounded-full shadow-sm border border-white/20 w-fit ${rank.bg} ${rank.text}`}>
                            <Award size={12} className="mr-1" />
                            <span className="text-[10px] font-black uppercase tracking-tighter">{rank.label}</span>
                        </div>
                    </div>

                    <button onClick={handleFavorite} disabled={isFavoriting} className="absolute top-3 right-12 p-2 rounded-full hover:bg-red-50 disabled:opacity-50 transition-transform active:scale-125 z-10">
                        <Heart className={`w-6 h-6 ${isFavorited ? 'text-red-500 fill-current' : 'text-gray-400'}`} />
                    </button>
                    <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-200 z-10">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>

                    {/* Cabeçalho Reorganizado - Foto Squircle */}
                    <div className="p-6 pt-16 pb-4">
                        <div className="flex items-center space-x-5">
                            {/* Container da Logo Estilo Squircle */}
                            <div className="w-24 h-24 rounded-2xl overflow-hidden border-4 border-white shadow-md bg-white flex items-center justify-center p-1 flex-shrink-0">
                                <img
                                    src={profile.photoUrl || `https://ui-avatars.com/api/?name=${displayName.replace(/ /g, '+')}&background=F3F4F6&color=9CA3AF`}
                                    alt={displayName}
                                    className="w-full h-full object-contain"
                                />
                            </div>

                            <div className="flex flex-col pr-8 min-w-0">
                                <h2 className="text-xl font-bold text-gray-800 leading-tight truncate">{displayName}</h2>
                                <p className="font-semibold text-gray-500 text-sm truncate">{profile.mainCategory}</p>

                                <div className="flex items-center mt-2 space-x-2">
                                    <div className="flex items-center bg-white px-2 py-1 rounded-lg border shadow-sm w-fit">
                                        <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                                        <span className="text-sm font-bold text-gray-700">{(profile.rating || 0).toFixed(1)}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">{profile.ratingCount || 0} avaliações</span>
                                        <span className="text-[10px] text-blue-600 font-black uppercase tracking-tighter">{completedServices} atendimentos</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Navegação por Abas */}
                    <div className="px-6 border-b-2">
                        <div className="flex">
                            <button onClick={() => setActiveTab('about')} className={`flex-1 py-3 font-semibold text-sm transition-all ${activeTab === 'about' ? 'border-b-4 border-blue-600 text-blue-600' : 'text-gray-500'}`}>Sobre</button>
                            <button onClick={() => setActiveTab('portfolio')} className={`flex-1 py-3 font-semibold text-sm transition-all ${activeTab === 'portfolio' ? 'border-b-4 border-blue-600 text-blue-600' : 'text-gray-500'}`}>Portfólio</button>
                            <button onClick={() => setActiveTab('reviews')} className={`flex-1 py-3 font-semibold text-sm transition-all ${activeTab === 'reviews' ? 'border-b-4 border-blue-600 text-blue-600' : 'text-gray-500'}`}>Avaliações</button>
                        </div>
                    </div>

                    {/* Conteúdo da Aba */}
                    <div className="p-6 flex-grow overflow-y-auto">
                        {renderTabContent()}
                    </div>

                    {/* Rodapé de Ação */}
                    <div className="p-6 bg-white border-t-2">
                        {existingChatId ? (
                            <div className="text-center">
                                <p className="text-sm text-blue-700 bg-blue-50 p-3 rounded-lg border border-blue-100">Você já possui uma solicitação em aberto.</p>
                                <button onClick={onOpenChat} className="w-full mt-4 py-3 bg-green-600 text-white font-bold rounded-xl flex items-center justify-center shadow-md">
                                    <ArrowRightCircle className='w-5 h-5 mr-2' />Ver Solicitação
                                </button>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label htmlFor="service-title-input" className="block text-sm font-bold text-gray-500 uppercase tracking-wider">Qual serviço você precisa?</label>
                                        <span className={`text-[10px] font-bold ${serviceTitle.length >= 80 ? 'text-orange-500' : 'text-gray-400'}`}>
                                            {serviceTitle.length}/80
                                        </span>
                                    </div>
                                    <input
                                        type="text"
                                        id="service-title-input"
                                        name="service-title-input"
                                        value={serviceTitle}
                                        onChange={(e) => setServiceTitle(e.target.value)}
                                        maxLength={80}
                                        className="w-full px-4 py-2 border-2 border-gray-100 rounded-lg focus:border-blue-500 focus:outline-none transition-all font-semibold"
                                        placeholder="Ex: Instalação de chuveiro"
                                    />
                                </div>
                                <div className="mt-4">
                                    <button onClick={handleStartChat} disabled={isUnavailable} className={`w-full py-3 text-white font-bold rounded-xl flex items-center justify-center transition-all shadow-lg ${isUnavailable ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98]'}`}>
                                        <MessageSquare className='w-5 h-5 mr-2' />{isUnavailable ? 'Serviço Indisponível' : 'Solicitar Orçamento'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <Lightbox open={openLightbox} close={() => setOpenLightbox(false)} index={lightboxIndex} slides={slides} />

            {/* Modal de Todas as Avaliações */}
            {isAllReviewsModalOpen && (
                <AllReviewsModal
                    serviId={serviId}
                    serviName={displayName}
                    onClose={() => setIsAllReviewsModalOpen(false)}
                />
            )}
        </>
    );
};

export default ServiDetailModal;
